import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';
import type { VineCartProps, ContentfulOfferProps } from 'tsconfig/types.d';
import { globals } from 'common-ui';

interface CartState {
  cart: VineCartProps;
  subscribeSaveContentfulOffer: ContentfulOfferProps[] | null;
}

interface CartActions {
  setCart: (cart: VineCartProps) => void;
  updateCart: (updater: (prev: VineCartProps) => VineCartProps) => void;
  setSubscribeSaveContentfulOffer: (offers: ContentfulOfferProps[] | null) => void;
  reset: () => void;
}

export type CartStore = CartState & CartActions;

const initialCart: VineCartProps = {
  cartIsLoaded: false,
  cartItems: undefined,
  cartError: {
    hasCartError: false,
    cartErrorMessage: '',
  },
};

export const useCartStore = create<CartStore>()(
  devtools(
    set => ({
      cart: initialCart,
      subscribeSaveContentfulOffer: null,
      setCart: cart => set({ cart }, undefined, 'setCart'),
      updateCart: updater => set(state => ({ cart: updater(state.cart) }), undefined, 'updateCart'),
      setSubscribeSaveContentfulOffer: offers =>
        set({ subscribeSaveContentfulOffer: offers }, undefined, 'setSubscribeSaveContentfulOffer'),
      reset: () => set({ cart: initialCart, subscribeSaveContentfulOffer: null }, undefined, 'reset'),
    }),
    { name: 'CartStore' },
  ),
);

// Derived selectors
export const selectCartDetails = (state: CartState) => {
  const { cartItems } = state.cart;

  if (!cartItems?.totalItemQuantity) {
    return { itemsCount: 0, cartTotal: 0, hasQuantity: false };
  }

  let cartTotal = cartItems.totalPrice?.amount ?? 0;

  if (globals.useNewCartAPI) {
    const shippingPrice = cartItems?.shippingInformation?.price?.amount ?? 0;
    cartTotal = Math.max(cartTotal - shippingPrice, 0);
  }

  return {
    itemsCount: cartItems.totalItemQuantity,
    cartTotal,
    hasQuantity: cartItems.totalItemQuantity > 0,
  };
};

export const selectCartData = (state: CartState) => state.cart;

// Pre-wrapped hooks — use these in components instead of useCartStore(selector)
export const useCartDetails = () => useCartStore(useShallow(selectCartDetails));
export const useCartData = () => useCartStore(useShallow(selectCartData));
